/**
 * Dates as they read on screen — "Sep 11, 2026", "Jan 2023".
 *
 * The canonical rule is formatters.ts in the back office, which reaches the same strings through
 * date-fns. Both pin en-US rather than following the viewer, so the same row reads identically to
 * a UK reader and a US one; a numeric date cannot do that, since 11/09 and 9/11 are the same day
 * spelled two ways.
 */

const DAY_MONTH_YEAR: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

/** An instant — a DateTimeOffset from the API — on the day the viewer experienced it. */
export function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", DAY_MONTH_YEAR);
}

/** The day of the year an instant falls on, for something that recurs — "Dec 1". */
export function formatDayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

/** The same instant a year on. */
export function aYearOn(iso: string): string {
  const date = new Date(iso);
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString();
}

/**
 * A calendar month. Pinned to UTC because the API sends these as DateOnly: "2023-01-01" parses as
 * UTC midnight, which a negative-offset viewer would otherwise read as the previous December.
 */
export function formatMonthYear(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** A 24-hour clock, so a signal's time can't be misread between morning and evening. */
const TIME_OF_DAY: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

/** The time of day alone - "14:32". */
export function formatTimeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", TIME_OF_DAY);
}

/** A day as a heading - "Mon, Oct 5", with the year once it isn't this one. */
export function formatDayHeading(iso: string): string {
  const date = new Date(iso);
  const thisYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(thisYear ? {} : { year: "numeric" }),
  });
}

/** Whole calendar days from the day an instant fell on to the viewer's today: 0 for today, 1 for yesterday. */
export function calendarDaysAgo(iso: string): number {
  const day = new Date(iso);
  day.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - day.getTime()) / 86_400_000);
}

/** Whether two instants fell on the same day for the viewer. */
export function isSameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** When a signal arrived, to the minute - "Oct 3 · 14:32", with the year once it isn't this one. */
export function formatSignalTime(iso: string): string {
  const date = new Date(iso);
  const thisYear = date.getFullYear() === new Date().getFullYear();
  const day = thisYear ? formatDayMonth(iso) : formatDateShort(iso);
  const time = date.toLocaleTimeString("en-US", TIME_OF_DAY);
  return `${day} · ${time}`;
}

/** The time alone for a signal from today, otherwise its day - "14:32", "Oct 1". */
export function formatSignalTimeBrief(iso: string): string {
  const date = new Date(iso);
  const today = date.toDateString() === new Date().toDateString();
  return today ? date.toLocaleTimeString("en-US", TIME_OF_DAY) : formatDayMonth(iso);
}

/** An instant in full, with the viewer's time zone - "Oct 3, 2026, 14:32 GMT+1". */
export function formatDateTimeFull(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    ...DAY_MONTH_YEAR,
    ...TIME_OF_DAY,
    timeZoneName: "short",
  });
}
